/**
 * Dashboard GR line “As ordered” (Chunk A1–A2) — local check-in then Save as Full accept.
 * Mutual exclusive with Report Error.
 */

export function getReceiptingLineId(recordOrId) {
  if (recordOrId == null) return ''
  if (typeof recordOrId === 'object') {
    return String(recordOrId._id || recordOrId.order || '')
  }
  return String(recordOrId)
}

export function isLineAsOrdered(asOrderedLineIds = [], recordOrId) {
  const id = getReceiptingLineId(recordOrId)
  if (!id) return false
  return (asOrderedLineIds || []).some(lineId => String(lineId) === id)
}

function matchesPoSupplier(item, supplierId, purchaseOrderNumber) {
  if (!item || item.purchaseOrderNumber !== purchaseOrderNumber) return false
  const itemSupplierId = item?.supplier?._id || item?.supplier
  return String(itemSupplierId) === String(supplierId)
}

/**
 * Tick / untick As ordered. When checking, clears any Report Error row for the line.
 */
export function applyAsOrderedToggle({
  asOrderedLineIds = [],
  editedReceipting = [],
  recordOrId,
  checked,
} = {}) {
  const id = getReceiptingLineId(recordOrId)
  if (!id) {
    return { asOrderedLineIds, editedReceipting }
  }

  if (checked) {
    const nextIds = asOrderedLineIds.some(lineId => String(lineId) === id)
      ? asOrderedLineIds
      : [...asOrderedLineIds, id]
    return {
      asOrderedLineIds: nextIds,
      editedReceipting: editedReceipting.filter(
        row => getReceiptingLineId(row.order) !== id,
      ),
    }
  }

  return {
    asOrderedLineIds: asOrderedLineIds.filter(lineId => String(lineId) !== id),
    editedReceipting,
  }
}

/**
 * Tick / untick Report Error. When checking, clears As ordered for the line.
 * `nextErrorRow` is the row to add when checking (caller builds supplier/PO fields).
 */
export function applyReportErrorToggle({
  asOrderedLineIds = [],
  editedReceipting = [],
  recordOrId,
  checked,
  nextErrorRow = null,
} = {}) {
  const id = getReceiptingLineId(recordOrId)
  if (!id) {
    return { asOrderedLineIds, editedReceipting }
  }

  const withoutLine = editedReceipting.filter(
    row => getReceiptingLineId(row.order) !== id,
  )

  if (checked) {
    return {
      asOrderedLineIds: asOrderedLineIds.filter(
        lineId => String(lineId) !== id,
      ),
      editedReceipting: nextErrorRow
        ? [...withoutLine, nextErrorRow]
        : withoutLine,
    }
  }

  return {
    asOrderedLineIds,
    editedReceipting: withoutLine,
  }
}

/**
 * Build create-receipting payload rows for locally ticked As ordered lines.
 */
export function buildFullAcceptRowsFromAsOrdered({
  asOrderedLineIds = [],
  lineOrders = [],
  placedVia = '',
} = {}) {
  const byId = new Map(
    (lineOrders || []).map(line => [getReceiptingLineId(line), line]),
  )

  return (asOrderedLineIds || [])
    .map(id => byId.get(String(id)))
    .filter(Boolean)
    .map(line => ({
      order: line._id,
      supplier: line.supplier,
      purchaseOrderNumber: line.purchaseOrderNumber,
      status: 'Full accept',
      placedVia,
    }))
}

/**
 * Filter as-ordered ids that belong to this PO's loaded lines.
 */
export function filterAsOrderedIdsForPo(
  asOrderedLineIds = [],
  lineOrders = [],
) {
  const poIds = new Set(
    (lineOrders || []).map(line => getReceiptingLineId(line)).filter(Boolean),
  )
  return (asOrderedLineIds || []).filter(id => poIds.has(String(id)))
}

/**
 * PO receiptingStatus after Save, given the create payload rows.
 */
// Line edits that are reconciliation-only: they never create a supplier
// credit request, so the PO must not be labelled as a credit status.
const RECONCILIATION_ONLY_ERRORS = [
  'Weighted item',
  'Supplier / Invoice adjustment',
]

export function resolveSaveReceiptingStatus(saveType, data = []) {
  if (saveType === 'cn_request') return 'CN Requested'
  if (
    data.length > 0 &&
    data.every(item => (item.status || '') === 'Full accept')
  ) {
    return 'Full accept'
  }
  const hasSupplierReportedError = data.some(
    item => item.error && !RECONCILIATION_ONLY_ERRORS.includes(item.error),
  )
  if (hasSupplierReportedError) {
    return 'Sent to supplier'
  }
  // Only weighted / invoice-adjustment edits with no credit request: the
  // discrepancy is reviewed during invoice reconciliation — it is NOT a
  // credit approval.
  return 'Awaiting invoice reconciliation'
}

/**
 * Merge error / weighted / As ordered rows for Save (errors win over As ordered).
 */
function resolveOrderedQuantityForLine(item = {}, lineOrders = []) {
  const lineId = getReceiptingLineId(item?.order)
  const line = (lineOrders || []).find(
    row => getReceiptingLineId(row) === lineId,
  )
  const fromItem = parseFiniteQuantity(item?.orderedQuantity)
  const fromLineOrdered = parseFiniteQuantity(line?.orderedQuantity)
  const fromLineQty = parseFiniteQuantity(line?.quantity)
  if (!Number.isNaN(fromItem) && fromItem > 0) return fromItem
  if (!Number.isNaN(fromLineOrdered) && fromLineOrdered > 0) {
    return fromLineOrdered
  }
  if (!Number.isNaN(fromLineQty) && fromLineQty > 0) return fromLineQty
  return 0
}

function parseFiniteQuantity(value) {
  if (value === null || value === undefined || value === '') {
    return NaN
  }
  const num = Number(value)
  return Number.isFinite(num) ? num : NaN
}

function normalizeReceiptingSaveRow(item = {}, lineOrders = []) {
  const orderedQty = resolveOrderedQuantityForLine(item, lineOrders)

  if (item?.error === 'Not Delivered') {
    return {
      ...item,
      updatedQuantity: 0,
      orderedQuantity: orderedQty,
      previousQty:
        item?.previousQty != null && item?.previousQty !== ''
          ? item.previousQty
          : orderedQty,
    }
  }

  if (item?.error === 'Weighted item') {
    const weight = parseFiniteQuantity(item?.weight)
    if (!Number.isNaN(weight)) {
      // Keep an explicitly entered case count (Received Qty) separate from
      // the billable weight so the server stores both independently.
      // Legacy: when no case count was entered, the weight doubles as the
      // received quantity exactly as before.
      const explicitCases = parseFiniteQuantity(item?.updatedQuantity)
      return {
        ...item,
        ...(Number.isNaN(explicitCases) ? { updatedQuantity: weight } : {}),
        orderedQuantity: orderedQty,
      }
    }
  }

  return item
}

export function buildSaveReceiptingData({
  editedReceipting = [],
  weightedItems = [],
  asOrderedLineIds = [],
  lineOrders = [],
  supplierId,
  purchaseOrderNumber,
  placedVia = '',
} = {}) {
  const errorRows = (editedReceipting || []).filter(item =>
    matchesPoSupplier(item, supplierId, purchaseOrderNumber),
  )
  const weightedRows = (weightedItems || []).filter(item =>
    matchesPoSupplier(item, supplierId, purchaseOrderNumber),
  )
  const errorIds = new Set(
    errorRows.map(row => getReceiptingLineId(row.order)).filter(Boolean),
  )
  const asOrderedIds = filterAsOrderedIdsForPo(
    asOrderedLineIds,
    lineOrders,
  ).filter(id => !errorIds.has(String(id)))

  const fullAcceptRows = buildFullAcceptRowsFromAsOrdered({
    asOrderedLineIds: asOrderedIds,
    lineOrders,
    placedVia,
  })

  const weightedRemaining = [...weightedRows]
  const mergedErrors = errorRows.map(item => {
    const index = weightedRemaining.findIndex(
      w => getReceiptingLineId(w.order) === getReceiptingLineId(item.order),
    )
    if (index < 0) return item
    const [weightedItem] = weightedRemaining.splice(index, 1)
    return {
      ...item,
      weight: weightedItem.weight,
      orderedQuantity: weightedItem.orderedQuantity,
    }
  })

  return [
    ...fullAcceptRows,
    ...mergedErrors.map(item => normalizeReceiptingSaveRow(item, lineOrders)),
    ...weightedRemaining.map(item =>
      normalizeReceiptingSaveRow(item, lineOrders),
    ),
  ]
}

/**
 * Clear As ordered ids that belong to the saved PO.
 */
export function clearAsOrderedIdsForPo(asOrderedLineIds = [], lineOrders = []) {
  const poIds = new Set(
    (lineOrders || []).map(line => getReceiptingLineId(line)).filter(Boolean),
  )
  return (asOrderedLineIds || []).filter(id => !poIds.has(String(id)))
}
